/**
 * Recipient logic for the Send BOL dialog (single-load and bulk).
 *
 * Pure (no React) so it can be unit tested and reused across the manage-load
 * dialog and the bulk Send BOL action.
 */

export interface BOLRecipientSource {
  /** Stable key — a contact id, or the address itself for carrier-level emails. */
  id: string;
  email: string;
  name?: string | null;
  /** DISPATCHER / DRIVER, or null for an address off the carrier record. */
  type?: string | null;
  /** True for the contact currently assigned to this load. */
  assigned?: boolean;
}

/** Deliberately loose: catches typos and empty segments, not RFC 5322. */
export const EMAIL_PATTERN = /^[^@\s,]+@[^@\s,]+\.[^@\s,]+$/;

/** Splits the free-text field on commas. Newlines too — pasted lists carry them. */
export function parseEmailList(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/** Case-insensitive dedupe, first spelling wins. Mirrors what the server does. */
export function dedupeEmails(emails: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const email of emails) {
    const key = email.trim().toLowerCase();
    if (!key || seen.has(key)) continue;

    seen.add(key);
    out.push(email.trim());
  }

  return out;
}

/**
 * Collapses the carrier's contact cards and its carrier-level addresses into one
 * de-duplicated list, contacts first so the named people lead.
 */
export function buildRecipientSources(
  contacts: any[] | undefined,
  carrierEmails: string[] | undefined,
  assignedContactId?: string | null
): BOLRecipientSource[] {
  const seen = new Set<string>();
  const sources: BOLRecipientSource[] = [];

  for (const contact of contacts ?? []) {
    const email = (contact?.email ?? '').trim();
    if (!email || seen.has(email.toLowerCase())) continue;

    seen.add(email.toLowerCase());
    sources.push({
      id: contact.id ?? email,
      email,
      name: contact.name ?? null,
      type: contact.type ?? null,
      assigned: Boolean(assignedContactId && contact.id === assignedContactId),
    });
  }

  for (const raw of carrierEmails ?? []) {
    const email = (raw ?? '').trim();
    if (!email || seen.has(email.toLowerCase())) continue;

    seen.add(email.toLowerCase());
    sources.push({ id: email, email });
  }

  return sources;
}

/**
 * Who is ticked when the dialog opens: the contact assigned to this load, and
 * only them. The admin adds anyone else deliberately.
 *
 * With no assigned contact it falls back the way the old single-recipient field
 * did — a dispatcher, else whatever is first — so the default recipient is
 * unchanged from before the dialog went multi-select.
 */
export function defaultRecipientIds(sources: BOLRecipientSource[]): string[] {
  const assigned = sources.find((source) => source.assigned);
  if (assigned) return [assigned.id];

  const dispatcher = sources.find((source) => source.type === 'DISPATCHER');
  if (dispatcher) return [dispatcher.id];

  return sources.length ? [sources[0].id] : [];
}
